import React from 'react';
import Drawer from '@mui/material/Drawer';
import omit from 'lodash/fp/omit';
import EntityContentType from '@/constants/EntityContentType';
import useQuery from '@/lib/hooks/useQuery';
import useEntityDetails from '../lib/useEntityDetails';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import EntityDetailsContent from './EntityDetailsContent';
import OpportunityEntityDetails from './OpportunityEntityDetails';

const PAPER_PROPS = {
  className: 'max-w-xl p-6 type-body1 w-full',
  'data-testid': 'entity-details',
};

type EntityPropsState = {
  constituentId: null | string;
  entityId: string;
  entityType: EntityContentType;
  onClose: () => void;
  SlideProps: {
    appear: boolean;
    mountOnEnter: boolean;
    unmountOnExit: boolean;
    onExited: () => void;
  };
};

export default function EntityDetails() {
  const { closeEntity } = useEntityDetails();
  const query = useQuery();
  const [entityProps, setEntityProps] = React.useState<{
    [k: string]: EntityPropsState;
  }>({});
  const [activeEntity, setActiveEntity] = React.useState('');

  React.useEffect(() => {
    const queryEntityId = query.get('entity_id');
    const queryEntityType = query.get('entity_type');

    if (!queryEntityId && activeEntity) {
      setActiveEntity('');
    } else if (
      (queryEntityId && !activeEntity) ||
      (queryEntityId && activeEntity && queryEntityId !== activeEntity)
    ) {
      setActiveEntity(queryEntityId);

      const props: EntityPropsState = {
        constituentId: query.get('entity_constituent_id'),
        entityId: queryEntityId,
        entityType: queryEntityType as EntityContentType,
        onClose: () => {
          closeEntity();
        },
        SlideProps: {
          appear: true,
          mountOnEnter: true,
          unmountOnExit: true,
          onExited: () => {
            // Only remove the exiting slide's props from the
            // internal state when it has completed its exit.
            setEntityProps((prevState) => omit(queryEntityId, prevState));
          },
        },
      };

      setEntityProps((prev) => ({
        ...prev,
        [queryEntityId]: props,
      }));
    }
  }, [activeEntity, query]);

  return Object.keys(entityProps).map((entityId) => {
    const { SlideProps, entityType, ...props } = entityProps[entityId];

    return (
      <Drawer
        key={entityId}
        anchor="right"
        open={entityId === activeEntity}
        onClose={props.onClose}
        PaperProps={PAPER_PROPS}
        SlideProps={SlideProps}
      >
        {entityType === EntityContentType.OPPORTUNITY && (
          <OpportunityEntityDetails
            onClose={props.onClose}
            opportunityId={entityId}
          />
        )}

        {entityType !== EntityContentType.OPPORTUNITY && (
          <EntityDetailsContent
            key={entityId}
            entityType={entityType}
            {...props}
          />
        )}
      </Drawer>
    );
  });
}
